import React, { useCallback, useEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { FeedbackButton } from '@atlassian/search-common/feedback-button';
import type { ExperimentConfig } from '@atlassian/search-experiment/types';

import { type ApplicationModes } from '../../constants/application-modes';
import { type PrimaryProductKey, type ProductKeys } from '../../constants/products';
import type { UserDetailsWithStatus } from '../../constants/types';
import { getExtensionData } from '../../utils/relay/get-extension-data';
import FeedbackCollector from '../feedback-collector';

const messages = defineMessages({
	giveFeedbackText: {
		id: 'give_feedback_text',
		defaultMessage: 'Give feedback',
		description: 'Message shown to prompt users to start searching',
	},
});

export interface FeedbackProps {
	cloudId: string | undefined;
	experimentConfig: ExperimentConfig;
	primaryProduct: PrimaryProductKey | undefined;
	selectedProducts: ProductKeys[];
	user: UserDetailsWithStatus;
	applicationMode: ApplicationModes;
	source: string;
	appearance?: 'subtle' | 'link';
	buttonRef?: React.RefObject<HTMLButtonElement>;
	requestId?: string;
	traceId?: string;
	isCQL?: boolean;
}

/**
 * Renders a give feedback button and controls whether to display the feedback form.
 */
const Feedback = ({
	cloudId,
	primaryProduct,
	experimentConfig,
	selectedProducts,
	user,
	applicationMode,
	source,
	appearance,
	buttonRef,
	requestId,
	traceId,
	isCQL,
}: FeedbackProps): React.JSX.Element => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [traceIdForFeedbackCollector, setTraceIdForFeedbackCollector] = useState('');
	const [requestIdForFeedbackCollector, setRequestIdForFeedbackCollector] = useState('');

	useEffect(() => {
		if (traceId) {
			setTraceIdForFeedbackCollector(traceId);
		}
	}, [traceId]);

	useEffect(() => {
		if (requestId) {
			setRequestIdForFeedbackCollector(requestId);
		}
	}, [requestId]);

	const updateTraceAndRequestIdFromRelay = useCallback(() => {
		const { traceId: traceIdForRelay } = getExtensionData(isCQL || false);
		if (traceIdForRelay && traceIdForRelay !== traceIdForFeedbackCollector) {
			setTraceIdForFeedbackCollector(traceIdForRelay);
			// using traceId for now, for backwards compatibility, this will be updated with integration test later
			setRequestIdForFeedbackCollector(traceIdForRelay);
		}
	}, [traceIdForFeedbackCollector, isCQL]);

	return (
		<>
			{appearance === 'subtle' ? (
				<Button
					appearance="subtle"
					onClick={(e) => {
						updateTraceAndRequestIdFromRelay();
						e.preventDefault();
						setIsOpen(true);
					}}
					iconBefore={() => <FeedbackIcon color="currentColor" label="Give feedback" />}
					ref={buttonRef}
				>
					{intl.formatMessage(messages.giveFeedbackText)}
				</Button>
			) : (
				<FeedbackButton
					onClick={(e) => {
						updateTraceAndRequestIdFromRelay();
						e.preventDefault();
						setIsOpen(true);
					}}
				/>
			)}
			<FeedbackCollector
				cloudId={cloudId || ''}
				experimentConfig={experimentConfig}
				isOpen={isOpen}
				primaryProduct={primaryProduct || ''}
				product={selectedProducts.length ? selectedProducts.join(', ') : 'no product filter'}
				source={source}
				setIsOpen={setIsOpen}
				userId={user.id || ''}
				userName={user.name || ''}
				mode={applicationMode}
				requestId={requestIdForFeedbackCollector}
				traceId={traceIdForFeedbackCollector}
			/>
		</>
	);
};

export default Feedback;
